import { useEffectPlease } from "@features/utils";
import _ from "lodash";
import { useCallback, useContext, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { CameraContext } from "./frame";

export type TransformType = {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  changed?: boolean;
  className?: string;
};

export const PanSizeComponent = ({
  readonly,
  className,
  ...props
}: {
  children?: React.ReactNode;
  width?: number;
  height?: number;
  startActive?: boolean;
  initialTransform?: Partial<TransformType>;
  transform?: Partial<TransformType>;
  onTransformChange?: (transform: TransformType) => void;
  readonly?: boolean;
  className?: string;
}) => {
  const [active, setActive] = useState(props.startActive || false);
  const frameContextState = useContext(CameraContext)?.context || {
    scale: 1,
    snapping: true,
    readonly: true,
  };

  const transform = useRef<TransformType>({
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    ...props.initialTransform,
  });

  const frameContext = useRef(frameContextState);
  const componentParent = useRef<HTMLDivElement | null>(null);
  const componentRenderer = useRef<HTMLDivElement | null>(null);
  const smoothTimeout = useRef<NodeJS.Timeout | null>(null);

  const grabberTopLeft = useRef<HTMLDivElement | null>(null);
  const grabberTopRight = useRef<HTMLDivElement | null>(null);
  const grabberBottomLeft = useRef<HTMLDivElement | null>(null);
  const grabberBottomRight = useRef<HTMLDivElement | null>(null);
  const grabberTop = useRef<HTMLDivElement | null>(null);
  const grabberBottom = useRef<HTMLDivElement | null>(null);
  const grabberLeft = useRef<HTMLDivElement | null>(null);
  const grabberRight = useRef<HTMLDivElement | null>(null);
  const grabberRotate = useRef<HTMLDivElement | null>(null);

  const grabState = useRef<{
    // Before transform
    previous_transform: {
      x: number;
      y: number;
      scaleX: number;
      scaleY: number;
      rotation: number;
    };

    // Cursor start position
    x: number;
    y: number;
    action: "resize" | "rotate" | "move" | null;

    // Relative point for resizing or rotating
    relative_to_x?: number;
    relative_to_y?: number;

    // ScaleConstraints
    scale_x_constraint?: boolean;
    scale_y_constraint?: boolean;
  }>({
    x: 0,
    y: 0,
    action: null,
    previous_transform: { x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0 },
  });

  const applyTransform = useCallback(
    (smooth = false, save = false) => {
      if (componentParent.current) {
        if (smoothTimeout.current) clearTimeout(smoothTimeout.current);

        if (smooth) {
          componentParent.current!.style.transition = "transform 2s ease-out";
        } else {
          componentParent.current!.style.transition = "none";
        }

        componentParent.current!.style.transform = `translate(calc(${transform.current.x}px), calc(${transform.current.y}px)) rotate(${transform.current.rotation}deg) scale(${transform.current.scaleX}, ${transform.current.scaleY})`;
        const grabberFixScale = `scale(${
          1 / (frameContext.current.scale * transform.current.scaleX)
        }, ${1 / (frameContext.current.scale * transform.current.scaleY)})`;
        grabberBottom.current!.style.transform = grabberFixScale;
        grabberTop.current!.style.transform = grabberFixScale;
        grabberLeft.current!.style.transform = grabberFixScale;
        grabberRight.current!.style.transform = grabberFixScale;
        grabberTopLeft.current!.style.transform = grabberFixScale;
        grabberTopRight.current!.style.transform = grabberFixScale;
        grabberBottomLeft.current!.style.transform = grabberFixScale;
        grabberBottomRight.current!.style.transform = grabberFixScale;
        grabberRotate.current!.style.transform = grabberFixScale;

        if (smooth)
          smoothTimeout.current = setTimeout(() => {
            if (componentParent.current)
              componentParent.current!.style.transition = "none";
          }, 2000);
      }

      if (save) props.onTransformChange?.(transform.current);
    },
    [componentParent, props]
  );

  useEffectPlease(() => {
    frameContext.current = frameContextState;
    applyTransform();
  }, [frameContextState]);

  useEffectPlease(() => {
    if (props.transform && !_.isEqual(props.transform, transform.current)) {
      transform.current = {
        ...transform.current,
        ...props.transform,
      };
      applyTransform();
    }
  }, [props.transform]);

  const applyFrameTransform = useCallback(
    (point: number = 0, axis: "x" | "y") => {
      return point / frameContext.current.scale;
    },
    []
  );

  const grabResize = useCallback(
    (e: React.MouseEvent, grabber: string) => {
      e.preventDefault();
      grabState.current.action = "resize";
      grabState.current.previous_transform = { ...transform.current };
      grabState.current.x = applyFrameTransform(e.clientX, "x");
      grabState.current.y = applyFrameTransform(e.clientY, "y");

      // Relative point to decide
      // If alt key is pressed, use center of the element
      const center = {
        y:
          ((applyFrameTransform(getObjectY(grabberTopLeft.current), "y") || 0) +
            (applyFrameTransform(getObjectY(grabberBottomRight.current), "y") ||
              0)) /
          2,
        x:
          ((applyFrameTransform(getObjectX(grabberTopLeft.current), "x") || 0) +
            (applyFrameTransform(getObjectX(grabberBottomRight.current), "x") ||
              0)) /
          2,
      };
      grabState.current.scale_y_constraint = false;
      grabState.current.scale_x_constraint = false;
      if (e.altKey) {
        grabState.current.relative_to_y = center.y;
        grabState.current.relative_to_x = center.x;
      } else {
        // Otherwise use the opposite corner
        switch (grabber) {
          case "top-left":
            grabState.current.relative_to_x =
              applyFrameTransform(
                getObjectX(grabberBottomRight.current),
                "x"
              ) || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(
                getObjectY(grabberBottomRight.current),
                "y"
              ) || 0;
            break;
          case "top-right":
            grabState.current.relative_to_x =
              applyFrameTransform(getObjectX(grabberTopLeft.current), "x") || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(
                getObjectY(grabberBottomRight.current),
                "y"
              ) || 0;
            break;
          case "bottom-left":
            grabState.current.relative_to_x =
              applyFrameTransform(
                getObjectX(grabberBottomRight.current),
                "x"
              ) || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(getObjectY(grabberTopLeft.current), "y") || 0;
            break;
          case "bottom-right":
            grabState.current.relative_to_x =
              applyFrameTransform(getObjectX(grabberTopLeft.current), "x") || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(getObjectY(grabberTopLeft.current), "y") || 0;
            break;
          case "top":
            grabState.current.scale_y_constraint = true;
            grabState.current.relative_to_x =
              applyFrameTransform(getObjectX(grabberBottom.current), "x") || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(getObjectY(grabberBottom.current), "y") || 0;
            break;
          case "bottom":
            grabState.current.scale_y_constraint = true;
            grabState.current.relative_to_x =
              applyFrameTransform(getObjectX(grabberTop.current), "x") || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(getObjectY(grabberTop.current), "y") || 0;
            break;
          case "left":
            grabState.current.scale_x_constraint = true;
            grabState.current.relative_to_x =
              applyFrameTransform(getObjectX(grabberRight.current), "x") || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(getObjectY(grabberRight.current), "y") || 0;
            break;
          case "right":
            grabState.current.scale_x_constraint = true;
            grabState.current.relative_to_x =
              applyFrameTransform(getObjectX(grabberLeft.current), "x") || 0;
            grabState.current.relative_to_y =
              applyFrameTransform(getObjectY(grabberLeft.current), "y") || 0;
            break;
          default:
            grabState.current.relative_to_x = center.x;
            grabState.current.relative_to_y = center.y;
            break;
        }
      }
    },
    [applyFrameTransform]
  );

  const grabRotate = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      grabState.current.action = "rotate";
      grabState.current.previous_transform = { ...transform.current };
      grabState.current.x = applyFrameTransform(e.clientX, "x");
      grabState.current.y = applyFrameTransform(e.clientY, "y");

      const center = {
        y:
          ((applyFrameTransform(getObjectY(grabberTopLeft.current), "y") || 0) +
            (applyFrameTransform(getObjectY(grabberBottomRight.current), "y") ||
              0)) /
          2,
        x:
          ((applyFrameTransform(getObjectX(grabberTopLeft.current), "x") || 0) +
            (applyFrameTransform(getObjectX(grabberBottomRight.current), "x") ||
              0)) /
          2,
      };
      grabState.current.relative_to_x = center.x;
      grabState.current.relative_to_y = center.y;
    },
    [applyFrameTransform]
  );

  const grabMove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      grabState.current.action = "move";
      grabState.current.previous_transform = { ...transform.current };
      grabState.current.x = applyFrameTransform(e.clientX, "x");
      grabState.current.y = applyFrameTransform(e.clientY, "y");
    },
    [applyFrameTransform]
  );

  const applyTwoPointsTransform = useCallback(
    (
      pointAX: number,
      pointAY: number,
      pointBX: number,
      pointBY: number,
      pointABeforeX: number,
      pointABeforeY: number,
      pointBBeforeX: number,
      pointBBeforeY: number,
      constraints: {
        scale_x_constraint?: boolean;
        scale_y_constraint?: boolean;
        rotation_constraint?: boolean;
      } = {}
    ) => {
      // Apply the transform diff that moves the point Abefore to the point A and same for B
      // 1. Rotation
      // 2. Scale
      // 3. Translation

      if (constraints?.rotation_constraint) {
        const A = projectPointOntoLine(
          pointAX,
          pointAY,
          pointABeforeX,
          pointABeforeY,
          pointBBeforeX,
          pointBBeforeY
        );
        const B = projectPointOntoLine(
          pointBX,
          pointBY,
          pointABeforeX,
          pointABeforeY,
          pointBBeforeX,
          pointBBeforeY
        );

        pointAX = A.x;
        pointAY = A.y;
        pointBX = B.x;
        pointBY = B.y;
      }

      const axisBeforeX = pointBBeforeX - pointABeforeX;
      const axisBeforeY = pointBBeforeY - pointABeforeY;
      const axisX = pointBX - pointAX;
      const axisY = pointBY - pointAY;

      // Calculate the angle of rotation
      const rotation =
        (Math.atan2(axisY, axisX) - Math.atan2(axisBeforeY, axisBeforeX)) *
        (180 / Math.PI);

      // Calculate the scale factors
      const axisBeforeSize = Math.sqrt(
        axisBeforeX * axisBeforeX + axisBeforeY * axisBeforeY
      );
      const axisSize = Math.sqrt(axisX * axisX + axisY * axisY);
      let scaleX = axisSize / axisBeforeSize;
      let scaleY = scaleX;
      if (constraints?.scale_x_constraint) {
        scaleY = 1;
      } else if (constraints?.scale_y_constraint) {
        scaleX = 1;
      }

      // Calculate the new position from the barycenter diff
      const barycenterBeforeX = axisBeforeX / 2 + pointABeforeX;
      const barycenterBeforeY = axisBeforeY / 2 + pointABeforeY;
      const barycenterX = axisX / 2 + pointAX;
      const barycenterY = axisY / 2 + pointAY;

      const translationX = barycenterX - barycenterBeforeX;
      const translationY = barycenterY - barycenterBeforeY;

      return {
        scaleX,
        scaleY,
        rotation,
        translationX,
        translationY,
      };
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (grabState.current.action === "resize") {
        const allowDeformation = e.shiftKey;

        const pointAX = applyFrameTransform(e.clientX, "x");
        const pointAY = applyFrameTransform(e.clientY, "y");

        const pointABeforeX = grabState.current.x;
        const pointABeforeY = grabState.current.y;

        const center = {
          y:
            ((applyFrameTransform(getObjectY(grabberTopLeft.current), "y") ||
              0) +
              (applyFrameTransform(
                getObjectY(grabberBottomRight.current),
                "y"
              ) || 0)) /
            2,
          x:
            ((applyFrameTransform(getObjectX(grabberTopLeft.current), "x") ||
              0) +
              (applyFrameTransform(
                getObjectX(grabberBottomRight.current),
                "x"
              ) || 0)) /
            2,
        };

        let pointBX = grabState.current.relative_to_x || 0;
        let pointBY = grabState.current.relative_to_y || 0;
        if (e.altKey) {
          pointBX = center.x;
          pointBY = center.y;
        }
        let pointBBeforeX = pointBX;
        let pointBBeforeY = pointBY;

        const { scaleX, scaleY, rotation, translationX, translationY } =
          applyTwoPointsTransform(
            pointAX,
            pointAY,
            pointBX,
            pointBY,
            pointABeforeX,
            pointABeforeY,
            pointBBeforeX,
            pointBBeforeY,
            {
              scale_x_constraint:
                grabState.current.scale_x_constraint && allowDeformation,
              scale_y_constraint:
                grabState.current.scale_y_constraint && allowDeformation,
              rotation_constraint: true,
            }
          );

        transform.current.scaleX =
          grabState.current.previous_transform.scaleX * scaleX;
        transform.current.scaleY =
          grabState.current.previous_transform.scaleY * scaleY;
        transform.current.rotation =
          grabState.current.previous_transform.rotation + rotation;
        transform.current.x =
          grabState.current.previous_transform.x + translationX;
        transform.current.y =
          grabState.current.previous_transform.y + translationY;
      } else if (grabState.current.action === "rotate") {
        // Three points: the axis of rotation, the initial point to make the base vector, and the current point to make a new vector and compute
        const initialVectorX =
          grabState.current.x - (grabState.current.relative_to_x || 0);
        const initialVectorY =
          grabState.current.y - (grabState.current.relative_to_y || 0);
        const currentVectorX =
          applyFrameTransform(e.clientX, "x") -
          (grabState.current.relative_to_x || 0);
        const currentVectorY =
          applyFrameTransform(e.clientY, "y") -
          (grabState.current.relative_to_y || 0);

        let angle =
          (Math.atan2(currentVectorY, currentVectorX) -
            Math.atan2(initialVectorY, initialVectorX)) *
          (180 / Math.PI);

        if (e.shiftKey || e.altKey) {
          let roundValue = 5;
          if (e.shiftKey && e.altKey) {
            roundValue = 22.5; // 16 angles (0, 22.5, 45, ..., 337.5)
          }
          // Round to nearest 5 degrees
          angle = Math.round(angle / roundValue) * roundValue;
        }

        transform.current.rotation =
          grabState.current.previous_transform.rotation + angle;
      } else if (grabState.current.action === "move") {
        let dx = applyFrameTransform(e.clientX, "x") - grabState.current.x;
        let dy = applyFrameTransform(e.clientY, "y") - grabState.current.y;

        if (e.shiftKey) {
          // If shift is pressed, move only in one direction
          if (Math.abs(dx) > Math.abs(dy)) {
            dy = 0; // Lock vertical movement
          } else {
            dx = 0; // Lock horizontal movement
          }
        }

        transform.current.x = grabState.current.previous_transform.x + dx;
        transform.current.y = grabState.current.previous_transform.y + dy;
      } else {
        return;
      }

      if (frameContext.current.snapping) {
        if (
          grabState.current.action === "move" &&
          Math.abs(transform.current.x) < transform.current.scaleX * 20 &&
          Math.abs(transform.current.y) < transform.current.scaleY * 20
        ) {
          transform.current.x = 0;
          transform.current.y = 0;
        }
        if (Math.abs(transform.current.rotation) < 5) {
          transform.current.rotation = 0; // Reset rotation if too small
        }
        if (
          grabState.current.action === "resize" &&
          Math.abs(transform.current.scaleX - 1) < 0.02 &&
          Math.abs(transform.current.scaleY - 1) < 0.02
        ) {
          transform.current.scaleX = 1;
          transform.current.scaleY = 1; // Reset scale if too small
          transform.current.x = grabState.current.previous_transform.x;
          transform.current.y = grabState.current.previous_transform.y; // Reset position if too small
        }
      }

      transform.current.changed = true;
      applyTransform();
    },
    [applyFrameTransform, applyTransform, applyTwoPointsTransform]
  );

  const handleMouseUp = useCallback(() => {
    if (grabState.current.action) {
      grabState.current.action = null; // Reset action on mouse up
      applyTransform(false, true); // Save the transform state
    }
  }, [applyTransform]);

  const touchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    // Save two points for touch
    if (e.touches.length >= 2) {
      grabState.current.action = "resize";
      grabState.current.previous_transform = { ...transform.current };
      grabState.current.x = e.touches[0].clientX;
      grabState.current.y = e.touches[0].clientY;
      grabState.current.relative_to_x = e.touches[1].clientX;
      grabState.current.relative_to_y = e.touches[1].clientY;
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        if (
          grabState.current.action === "resize" ||
          grabState.current.action === "rotate" ||
          grabState.current.action === "move"
        ) {
          const { scaleX, scaleY, rotation, translationX, translationY } =
            applyTwoPointsTransform(
              e.touches[0].clientX,
              e.touches[0].clientY,
              e.touches[1].clientX,
              e.touches[1].clientY,
              grabState.current.x,
              grabState.current.y,
              grabState.current.relative_to_x || 0,
              grabState.current.relative_to_y || 0
            );

          transform.current.scaleX =
            grabState.current.previous_transform.scaleX * scaleX;
          transform.current.scaleY =
            grabState.current.previous_transform.scaleY * scaleY;
          transform.current.rotation =
            grabState.current.previous_transform.rotation + rotation;
          transform.current.x =
            grabState.current.previous_transform.x + translationX;
          transform.current.y =
            grabState.current.previous_transform.y + translationY;
        }
      }
    },
    [applyTwoPointsTransform]
  );

  const handleMouseDown = useCallback((e: MouseEvent) => {
    // If parent component is part of things clicked
    if (
      componentParent.current &&
      componentParent.current.contains(e.target as Node)
    ) {
      setActive(true);
    } else {
      setActive(false);
    }
  }, []);

  useEffectPlease(() => {
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchstart", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchstart", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, []);

  if (frameContext.current.readonly || readonly) {
    return (
      <div
        className="relative"
        style={{
          width: props.width || 0,
          height: props.height || 0,
          transform: `translate(calc(${transform.current.x}px), calc(${transform.current.y}px)) rotate(${transform.current.rotation}deg) scale(${transform.current.scaleX}, ${transform.current.scaleY})`,
        }}
      >
        <div
          style={{
            width: props.width || 0,
            height: props.height || 0,
          }}
          className={twMerge("overflow-hidden", className)}
        >
          {props.children}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="absolute m-auto"
        style={{
          top: -100000,
          bottom: -100000,
          left: -100000,
          right: -100000,
          width: props.width || 0,
          height: props.height || 0,
        }}
      >
        <div
          ref={componentParent}
          className={twMerge("relative cursor-grab", active && "group/enabled")}
          style={{
            width: props.width || 0,
            height: props.height || 0,
            transform: `translate(calc(${transform.current.x}px), calc(${transform.current.y}px)) rotate(${transform.current.rotation}deg) scale(${transform.current.scaleX}, ${transform.current.scaleY})`,
          }}
        >
          <div
            ref={componentRenderer}
            onMouseDown={(e) => grabMove(e)}
            onTouchStart={(e) => touchStart(e)}
            style={{
              width: props.width || 0,
              height: props.height || 0,
            }}
            className={twMerge("overflow-hidden", className)}
          >
            {props.children}
          </div>

          <div
            ref={grabberTopLeft}
            className={twMerge(
              "z-10 w-4 h-4 absolute -top-2 -right-2 bg-white border border-slate-500 cursor-nesw-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "top-left")}
          />
          <div
            ref={grabberTopRight}
            className={twMerge(
              "z-10 w-4 h-4 absolute -top-2 -left-2 bg-white border border-slate-500 cursor-nwse-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "top-right")}
          />
          <div
            ref={grabberBottomLeft}
            className={twMerge(
              "z-10 w-4 h-4 absolute -bottom-2 -right-2 bg-white border border-slate-500 cursor-nwse-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "bottom-left")}
          />
          <div
            ref={grabberBottomRight}
            className={twMerge(
              "z-10 w-4 h-4 absolute -bottom-2 -left-2 bg-white border border-slate-500 cursor-nesw-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "bottom-right")}
          />

          <div
            ref={grabberLeft}
            className={twMerge(
              "z-10 w-4 h-4 absolute bottom-0 top-0 my-auto -left-2 bg-white border border-slate-500 cursor-ew-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "left")}
          />
          <div
            ref={grabberRight}
            className={twMerge(
              "z-10 w-4 h-4 absolute bottom-0 top-0 my-auto -right-2 bg-white border border-slate-500 cursor-ew-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "right")}
          />
          <div
            ref={grabberBottom}
            className={twMerge(
              "z-10 w-4 h-4 absolute left-0 right-0 mx-auto -bottom-2 bg-white border border-slate-500 cursor-ns-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "bottom")}
          />
          <div
            ref={grabberTop}
            className={twMerge(
              "z-10 w-4 h-4 absolute left-0 right-0 mx-auto -top-2 bg-white border border-slate-500 cursor-ns-resize opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabResize(e, "top")}
          />

          <div
            ref={grabberRotate}
            className={twMerge(
              "z-[5] w-px h-12 absolute left-0 right-0 mx-auto -top-12 bg-slate-500 opacity-0",
              active && "opacity-100"
            )}
            onMouseDown={(e) => grabRotate(e)}
            style={{
              transformOrigin: "center bottom",
            }}
          >
            <div className="z-10 w-4 h-4 absolute -left-2 -top-2 bg-yellow-500 border border-yellow-200 rounded-full cursor-grab" />
          </div>
        </div>
      </div>
    </>
  );
};

function projectPointOntoLine(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const vx = x2 - x1;
  const vy = y2 - y1;
  const len2 = vx * vx + vy * vy;
  if (len2 === 0) return { x: x1, y: y1 }; // degenerate line

  const t = ((px - x1) * vx + (py - y1) * vy) / len2; // unbounded projection
  // If you want the segment version instead:
  // const tt = Math.max(0, Math.min(1, t));

  return { x: x1 + t * vx, y: y1 + t * vy };
}

const getObjectX = (element: HTMLDivElement | null) => {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  return rect.left + rect.width / 2;
};

const getObjectY = (element: HTMLDivElement | null) => {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  return rect.top + rect.height / 2;
};
